import { skipToken } from '@reduxjs/toolkit/query';
import Divider from '@rio-cloud/rio-uikit/Divider';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Tag from '@rio-cloud/rio-uikit/Tag';
import { useGetTransportOrderQuery } from 'api/generated/transportOrdersApi';
import NoContentState from 'components/NoContentState';
import OrderStatusLabel from 'components/OrderStatusLabel';
import { useAppDispatch } from 'store/hooks';
import { formatDateTime } from 'utils/formatDateTime';
import { formatRoute, getOrderStatus } from 'utils/transportOrder';

import styles from './TransportOrderDetail.module.scss';
import TransportOrderDetailFooter from './TransportOrderDetailFooter';
import TransportOrderDetailMap from './TransportOrderDetailMap';
import TransportOrderDetailMilestones from './TransportOrderDetailMilestones';
import TransportOrderDetailRow from './TransportOrderDetailRow';
import TransportOrderDetailSkeleton from './TransportOrderDetailSkeleton';
import TransportOrderDetailSummary from './TransportOrderDetailSummary';
import { transportOrderDetailClosed } from './transportOrderDetailSlice';

const PANEL_WIDTH = 480;
const HEADER_CLASS = 'text-size-h4 text-normal padding-x-10';

const detailSkeleton = <TransportOrderDetailSkeleton />;

export type TransportOrderDetailProps = {
    id: string | null;
};

const TransportOrderDetail = ({ id }: TransportOrderDetailProps) => {
    const dispatch = useAppDispatch();

    const { currentData, isFetching, isError, refetch } = useGetTransportOrderQuery(
        id === null ? skipToken : { transportOrderId: id }
    );

    const handleEdit = () => {
        console.log('edit transport order', id);
    };

    const handleCancelOrder = () => {
        console.log('cancel transport order', id);
    };

    const renderContent = () => {
        if (isFetching && currentData === undefined) {
            return detailSkeleton;
        }
        if (isError) {
            return (
                <NoContentState
                    iconName='exclamation-sign'
                    headline='Detail could not be loaded'
                    action={{ text: 'Try again', onClick: refetch }}
                />
            );
        }
        if (currentData === undefined) {
            return null;
        }

        const status = currentData.deliveredAt === undefined ? getOrderStatus(currentData) : 'DELIVERED';

        return (
            <>
                <div className='margin-bottom-15'>
                    {status === undefined ? (
                        <Tag size='small' round={false} icon='rioglyph-truck'>
                            {currentData.vin}
                        </Tag>
                    ) : (
                        <OrderStatusLabel status={status} condensed={false} />
                    )}
                </div>

                <TransportOrderDetailMap order={currentData} />

                <TransportOrderDetailSummary order={currentData} />

                <TransportOrderDetailMilestones order={currentData} />

                <div className='margin-top-10 text-size-18 text-medium text-color-darkest'>Details</div>
                <Divider spacing={10} className={styles.divider} />
                <div className='display-grid grid-cols-2 gap-20'>
                    <TransportOrderDetailRow label='Load ID' value={currentData.loadId} />
                    <TransportOrderDetailRow label='Vehicle' value={currentData.vin} />
                    <TransportOrderDetailRow
                        label='Pickup window'
                        value={formatDateTime(currentData.pickupWindow.from)}
                        secondaryValue={formatDateTime(currentData.pickupWindow.to)}
                    />
                    <TransportOrderDetailRow
                        label='Delivery window'
                        value={formatDateTime(currentData.deliveryWindow.from)}
                        secondaryValue={formatDateTime(currentData.deliveryWindow.to)}
                    />
                </div>
            </>
        );
    };

    return (
        <Sidebar
            fly
            closed={!id}
            title={currentData === undefined ? 'Transport order' : formatRoute(currentData)}
            titleClassName={HEADER_CLASS}
            width={PANEL_WIDTH}
            position={Sidebar.RIGHT}
            footer={
                /* TODO: it will be necessary to double check whether the transport order is still cancellable or editable
                 *   because its state may have changed in the meantime. */
                currentData?.state === 'PLANNED' && (
                    <TransportOrderDetailFooter onEdit={handleEdit} onCancelOrder={handleCancelOrder} />
                )
            }
            onClose={() => {
                dispatch(transportOrderDetailClosed());
            }}
        >
            <div className='padding-x-20'>{renderContent()}</div>
        </Sidebar>
    );
};

export default TransportOrderDetail;
